import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Play, ArrowRight, ArrowUpRight, ShieldCheck, GraduationCap,
  Sparkles, Globe2, MessageCircle, Award
} from "lucide-react";
import { Section, Container, Eyebrow, DisplayH1, DisplayH2, Reveal } from "@/components/Layout";
import { StatNumber } from "@/components/StatNumber";
import Marquee from "@/components/Marquee";
import ProgramCard from "@/components/ProgramCard";
import FounderVideo from "@/components/FounderVideo";
import CesarTestimonial from "@/components/CesarTestimonial";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1553882809-a4f57e59501d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxnZXJtYW4lMjBzaGVwaGVyZCUyMGRvZyUyMHBvcnRyYWl0JTIwZGFyayUyMGNpbmVtYXRpY3xlbnwwfHx8fDE3ODU4MTczNjV8MA&ixlib=rb-4.1.0&q=85";

const PRESS = ["National Geographic", "Forbes", "The New York Times", "Wired", "Vogue", "Robb Report", "Financial Times", "GQ"];

const TESTIMONIALS = [
  {
    quote:
      "Wolfkeeper transformed my rescue Malinois in ways I did not think possible. This is the Harvard of dog training — nothing else compares.",
    name: "Dr. Amelia Vance",
    role: "Cardiovascular Surgeon · Chicago",
    image: "https://images.unsplash.com/photo-1599826023510-a98d08764aec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxiZWxnaWFuJTIwbWFsaW5vaXMlMjBkb2clMjB0cmFpbmluZyUyMGFjdGlvbnxlbnwwfHx8fDE3ODU4MTczNjV8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    quote:
      "As a service dog handler, I evaluated eleven programs across three continents. Wolfkeeper is the only one that felt like a genuine university.",
    name: "Captain Robert Ellery",
    role: "Retired US Navy · Service Dog Handler",
    image: "https://images.unsplash.com/photo-1602241628512-459cdd3234fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx0aGVyYXB5JTIwc2VydmljZSUyMGRvZyUyMGdvbGRlbiUyMHJldHJpZXZlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NTgxNzM2NXww&ixlib=rb-4.1.0&q=85",
  },
];

export default function Home() {
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data.programs?.slice(0, 6) || [])).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <>
      {/* HERO */}
      <Section testid="home-hero" className="relative h-[100svh] min-h-[720px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Wolfkeeper" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 cinema-overlay" />
          <div className="absolute inset-0 side-overlay" />
        </div>
        <Container className="relative z-10 h-full flex flex-col justify-end pb-20 lg:pb-28">
          <CesarTestimonial />
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
            className="max-w-4xl"
          >
            <Eyebrow>Est. 2002 · Chicago · Global</Eyebrow>
            <div className="mt-6 flex items-start gap-6">
              <img
                src="/logo-wolfkeeper-icon.png"
                alt="Wolfkeeper crest"
                className="hidden md:block h-24 w-auto drop-shadow-[0_10px_30px_rgba(212,175,55,0.35)]"
              />
              <DisplayH1>
                The world's most<br />elite canine university.
              </DisplayH1>
            </div>
            <p className="mt-8 text-[color:var(--wk-bone-dim)] text-lg md:text-xl max-w-2xl leading-relaxed">
              Twenty-four years of behavior science, humane methodology and cinematic
              apprenticeship — engineered for the working dogs, service dogs and families
              of the next century.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link to="/apply" data-testid="hero-apply" className="btn-gold">Start Training <ArrowRight size={16} /></Link>
              <Link to="/programs" data-testid="hero-explore" className="btn-ghost">Explore the University</Link>
              <button
                data-testid="hero-watch"
                onClick={() => nav("/media")}
                className="inline-flex items-center gap-3 text-sm text-[color:var(--wk-bone)]/90 hover:text-[color:var(--wk-gold)] group"
              >
                <span className="w-11 h-11 rounded-full hairline flex items-center justify-center group-hover:border-[color:var(--wk-gold)]">
                  <Play size={14} className="ml-0.5" />
                </span>
                Watch our story
              </button>
            </div>
          </motion.div>
        </Container>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center text-[color:var(--wk-bone-dim)]/70">
          <div className="overline text-[9px]">Scroll</div>
          <div className="w-px h-8 bg-white/30 mt-2 animate-pulse" />
        </div>
      </Section>

      {/* PRESS */}
      <Section testid="home-press">
        <Marquee items={PRESS} />
      </Section>

      {/* MANIFESTO */}
      <Section testid="home-manifesto" className="py-28 lg:py-40">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>The Manifesto</Eyebrow>
              <div className="mt-6 vrule h-24 lg:hidden" />
            </div>
            <Reveal className="lg:col-span-8">
              <DisplayH2>
                Every dog is a partnership.
                <span className="text-[color:var(--wk-bone-dim)]"> We teach the human side of it.</span>
              </DisplayH2>
              <p className="mt-10 text-lg text-[color:var(--wk-bone-dim)] max-w-3xl leading-relaxed">
                From the puppy who will become someone's medical alert dog, to the working
                Malinois trusted with a family's safety — every Wolfkeeper program is a study
                in behavior science, ethical craftsmanship, and human transformation.
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* LEGACY — Cesar + Trailblazer */}
      <Section testid="home-legacy" className="py-28 lg:py-36 border-y border-white/10 relative overflow-hidden">
        <Container>
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <Reveal className="lg:col-span-6">
              <FounderVideo />
              <div className="mt-6 grid grid-cols-5 gap-4">
                <div className="col-span-3 relative overflow-hidden rounded-2xl aspect-[4/5]">
                  <img
                    src="https://customer-assets-v7afamib.emergentagent.net/job_packleader-hub/artifacts/0f7iuk2g_Trailblazer.jpeg"
                    alt="Toriano 'The Wolfkeeper' Sanzone — Humane Society Trailblazer Award Winner 2025"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 ring-1 ring-inset ring-[color:var(--wk-gold)]/30 rounded-2xl pointer-events-none" />
                </div>
                <div className="col-span-2 flex flex-col gap-4">
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                    <img
                      src="https://customer-assets-v7afamib.emergentagent.net/job_packleader-hub/artifacts/foyq47ym_CESAR%20and%20I.jpeg"
                      alt="Founder Toriano Sanzone with Cesar Millan"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl pointer-events-none" />
                  </div>
                  <div className="card-lux p-5 text-center flex-1 flex flex-col justify-center">
                    <Award className="text-[color:var(--wk-gold)] mx-auto" size={22} />
                    <div className="mt-3 overline text-[10px]">Humane Society</div>
                    <div className="mt-1 font-serif-display text-xl leading-tight">Trailblazer<br />Award · 2025</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-xs text-[color:var(--wk-bone-dim)] text-center italic">
                Founder Toriano "The Wolfkeeper" Sanzone with Cesar Millan (right) · Cesar's Forum, Las Vegas 2025
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-6">
              <Eyebrow>The Founder's Lineage</Eyebrow>
              <DisplayH2 className="mt-4">
                Trained by the master.<br />
                <span className="text-[color:var(--wk-gold)]">Honored by the movement.</span>
              </DisplayH2>
              <p className="mt-8 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
                Wolfkeeper University was founded by <span className="text-[color:var(--wk-bone)]">Toriano "The Wolfkeeper" Sanzone</span> —
                personally trained by <span className="text-[color:var(--wk-bone)]">Cesar Millan</span>, the world's most recognized
                canine behaviorist. Twenty-plus years later, the student became the standard-bearer.
              </p>
              <p className="mt-4 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
                In 2025, the <span className="text-[color:var(--wk-bone)]">Humane Society of the United States</span> awarded
                Toriano its <span className="text-[color:var(--wk-gold)]">Trailblazer Award</span> — recognizing the unsung
                heroes in animal protection who are breaking barriers and forging new paths toward a more equitable and
                inclusive animal-welfare movement.
              </p>
              <blockquote className="mt-8 pl-6 border-l-2 border-[color:var(--wk-gold)] font-serif-display text-2xl italic text-[color:var(--wk-bone)] leading-snug">
                "I dedicate this Prestige Award to my Mother Tonia Sanzone and her love for dogs that she transferred to me at an early age."
                <div className="mt-3 not-italic text-sm text-[color:var(--wk-bone-dim)] font-body">— Toriano Sanzone, Trailblazer Award acceptance, Las Vegas 2025</div>
              </blockquote>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/about" data-testid="legacy-story" className="btn-gold">Read the full story <ArrowRight size={16} /></Link>
                <Link to="/media" className="btn-ghost">Watch the acceptance</Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* STATS */}
      <Section testid="home-stats" className="py-28 border-y border-white/10">
        <Container>
          <div className="grid gap-16 md:grid-cols-2 lg:grid-cols-4">
            {stats && (
              <>
                <StatNumber value={stats.dogs_trained} label="Dogs Trained" />
                <StatNumber value={stats.students_certified} label="Students Certified" />
                <StatNumber value={stats.countries_served} suffix="+" label="Countries Served" />
                <StatNumber value={stats.veterans_served} label="Veterans Served" />
              </>
            )}
          </div>
        </Container>
      </Section>

      {/* PROGRAMS */}
      <Section testid="home-programs" className="py-28 lg:py-36">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <Eyebrow>The Academies</Eyebrow>
              <DisplayH2 className="mt-4 max-w-3xl">
                Nine world-class programs. One relentless standard.
              </DisplayH2>
            </div>
            <Link to="/programs" data-testid="home-see-all-programs" className="btn-ghost h-11 shrink-0">
              See all programs <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {programs.map((p, i) => <ProgramCard key={p.slug} program={p} index={i} />)}
          </div>
        </Container>
      </Section>

      {/* PILLARS */}
      <Section testid="home-pillars" className="py-28 lg:py-36 border-t border-white/10">
        <Container>
          <div className="grid gap-10 md:grid-cols-4">
            {[
              { Icon: ShieldCheck, title: "Humane · Always", text: "Reward-based, evidence-informed. Never pain, force, or fear." },
              { Icon: GraduationCap, title: "University Standard", text: "Faculty, board of regents, published curriculum. Not a franchise." },
              { Icon: Sparkles, title: "Cinematic Craft", text: "Every session filmed. Every case documented. Every graduate a story." },
              { Icon: Globe2, title: "Global Reach", text: "Trainers across 38 countries. Ambassadors on six continents." },
            ].map(({ Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="flex flex-col gap-4">
                  <Icon className="text-[color:var(--wk-gold)]" size={26} />
                  <div className="font-serif-display text-2xl leading-tight">{title}</div>
                  <p className="text-sm text-[color:var(--wk-bone-dim)] leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* TESTIMONIALS */}
      <Section testid="home-testimonials" className="py-28 lg:py-36">
        <Container>
          <Eyebrow>Voices of the University</Eyebrow>
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="card-lux p-8 md:p-10 h-full flex flex-col">
                  <div className="text-[color:var(--wk-gold)] font-serif-display text-4xl leading-none">“</div>
                  <p className="mt-4 font-serif-display text-2xl md:text-3xl leading-snug">{t.quote}</p>
                  <div className="mt-8 flex items-center gap-4 pt-6 border-t border-white/10">
                    <img src={t.image} alt="" className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <div className="text-sm text-[color:var(--wk-bone)]">{t.name}</div>
                      <div className="text-xs text-[color:var(--wk-bone-dim)]">{t.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section testid="home-cta" className="py-28 lg:py-36 border-t border-white/10">
        <Container>
          <div className="relative overflow-hidden rounded-3xl">
            <img
              src="https://images.unsplash.com/photo-1641667838410-b257ca266e38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBkYXJrJTIwZm9yZXN0JTIwd29vZHMlMjBtaXN0fGVufDB8fHx8MTc4NTgxNzM2NXww&ixlib=rb-4.1.0&q=85"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative p-12 lg:p-24 grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <Eyebrow>Applications open</Eyebrow>
                <DisplayH2 className="mt-6">Begin your dog's transformation.</DisplayH2>
              </div>
              <div className="flex flex-col gap-4 lg:items-end">
                <p className="text-[color:var(--wk-bone-dim)] max-w-lg text-lg leading-relaxed">
                  Speak with our admissions team. We accept a limited number of applicants each
                  season across our nine academies.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/apply" data-testid="cta-apply" className="btn-gold">Apply Now</Link>
                  <Link to="/assistant" data-testid="cta-ai" className="btn-ghost">
                    <MessageCircle size={16} /> Meet TONIA — our AI Mentor
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
